import { 
  CreateTenantServiceModel, 
  UpdateTenantServiceModel, 
  TenantServiceResponseModel,
  BaseOperationResult,
  TenantPaginatedResult,
  PaginationParams,
  AuthTokens
} from '../types';
import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';

class TenantApiService extends AuthenticatedApiService {
  /**
   * Get paginated list of tenants
   */
  async getTenants(params: PaginationParams = {}): Promise<TenantPaginatedResult> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) queryParams.append('pageNumber', params.page.toString());
      if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());
      if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TENANTS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await this.makeAuthenticatedCall(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get tenants error:', error);
      throw error;
    }
  }

  /**
   * Get tenant by ID
   */
  async getTenantById(id: string): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TENANT_BY_ID(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get tenant by ID error:', error);
      throw error;
    }
  }

  /**
   * Create new tenant
   */
  async createTenant(tenant: CreateTenantServiceModel): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    try {
      const requestBody = JSON.stringify(tenant);
      console.log('POST /tenants request body:', requestBody); // Debug: verify code is in request
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TENANTS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: requestBody,
      });

      return await response.json();
    } catch (error) {
      console.error('Create tenant error:', error);
      throw error;
    }
  }

  /**
   * Update tenant
   */
  async updateTenant(id: string, tenant: UpdateTenantServiceModel): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TENANT_BY_ID(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(tenant),
      });

      return await response.json();
    } catch (error) {
      console.error('Update tenant error:', error);
      throw error;
    }
  }

  /**
   * Delete tenant
   */
  async deleteTenant(id: string): Promise<BaseOperationResult<boolean>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.TENANT_BY_ID(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Delete tenant error:', error);
      throw error;
    }
  }

  /**
   * Toggle tenant status (Active/Inactive)
   */
  async toggleTenantStatus(id: string, isActive: boolean): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    try {
      return await this.updateTenant(id, { isActive });
    } catch (error) {
      console.error('Toggle tenant status error:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const tenantApiService = new TenantApiService();

// Export the service with token management
export const tenantService = {
  /**
   * Initialize the service with tokens and refresh callback
   */
  initialize(tokens: AuthTokens | null, onTokenRefresh: (tokens: AuthTokens) => void) {
    tenantApiService.setTokens(tokens, onTokenRefresh);
  },

  /**
   * Get paginated list of tenants
   */
  async getTenants(params: PaginationParams = {}): Promise<TenantPaginatedResult> {
    return await tenantApiService.getTenants(params);
  },

  /**
   * Get tenant by ID
   */
  async getTenantById(id: string): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    return await tenantApiService.getTenantById(id);
  },

  /**
   * Create new tenant
   */
  async createTenant(tenant: CreateTenantServiceModel): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    return await tenantApiService.createTenant(tenant);
  },

  /**
   * Update tenant
   */
  async updateTenant(id: string, tenant: UpdateTenantServiceModel): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    return await tenantApiService.updateTenant(id, tenant);
  },

  /**
   * Delete tenant
   */
  async deleteTenant(id: string): Promise<BaseOperationResult<boolean>> {
    return await tenantApiService.deleteTenant(id);
  },

  /**
   * Toggle tenant status (Active/Inactive)
   */
  async toggleTenantStatus(id: string, isActive: boolean): Promise<BaseOperationResult<TenantServiceResponseModel>> {
    return await tenantApiService.toggleTenantStatus(id, isActive);
  },

  /**
   * Check if service is authenticated
   */
  isAuthenticated(): boolean {
    return tenantApiService.isAuthenticated();
  }
};
